/* eslint-disable react-refresh/only-export-components -- shared docs-site UI primitives */

import { useState } from "react";
import { copyTextToClipboard } from "@/utils/copyTextToClipboard";
import { DOMAIN_STATUS } from "../docsSiteConstants";

export function Toggle({ on, onToggle, id, disabled = false, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      id={id}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onToggle(!on);
      }}
      className={`inline-flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        on ? "ui-toggle-track-on" : "ui-toggle-track-off"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-slate-200/70 transition-transform duration-200 ease-out dark:bg-slate-200 dark:ring-slate-500 ${
          on ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

export function DevPreviewBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 ring-1 ring-violet-200/80 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-800/60">
      <i className="ri-code-box-line text-xs" aria-hidden />
      UI preview
    </span>
  );
}

export function StatusBadge({ status, size = "default" }) {
  const map = {
    [DOMAIN_STATUS.NOT_CONFIGURED]: {
      label: "Not configured",
      dot: "bg-slate-400",
      className: "bg-slate-50 text-slate-600 ring-slate-200/80 dark:bg-slate-800/80 dark:text-slate-300 dark:ring-slate-700",
    },
    [DOMAIN_STATUS.PENDING]: {
      label: "Pending verification",
      dot: "bg-amber-500 animate-pulse",
      className: "bg-amber-50 text-amber-800 ring-amber-200/70 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800/50",
    },
    [DOMAIN_STATUS.VERIFIED]: {
      label: "Verified",
      dot: "bg-sky-500",
      className: "bg-sky-50 text-sky-800 ring-sky-200/70 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/50",
    },
    [DOMAIN_STATUS.ACTIVE]: {
      label: "Live",
      dot: "bg-emerald-500",
      className: "bg-emerald-50 text-emerald-800 ring-emerald-200/70 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800/50",
    },
    [DOMAIN_STATUS.FAILED]: {
      label: "Failed",
      dot: "bg-rose-500",
      className: "bg-rose-50 text-rose-800 ring-rose-200/70 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-800/50",
    },
    draft: {
      label: "Draft",
      dot: "bg-slate-400",
      className: "bg-slate-50 text-slate-600 ring-slate-200/80 dark:bg-slate-800/80 dark:text-slate-300 dark:ring-slate-700",
    },
    published: {
      label: "Published",
      dot: "bg-emerald-500",
      className: "bg-emerald-50 text-emerald-800 ring-emerald-200/70 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800/50",
    },
    offline: {
      label: "Offline",
      dot: "bg-slate-400",
      className: "bg-slate-50 text-slate-600 ring-slate-200/80 dark:bg-slate-800/80 dark:text-slate-300 dark:ring-slate-700",
    },
    online: {
      label: "Online",
      dot: "bg-emerald-500 animate-pulse",
      className: "bg-emerald-50 text-emerald-800 ring-emerald-200/70 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800/50",
    },
  };

  const cfg = map[status] ?? map[DOMAIN_STATUS.NOT_CONFIGURED];
  const compact = size === "compact";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-bold uppercase tracking-wide ring-1 ${cfg.className} ${
        compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"
      }`}
    >
      <span className={`rounded-full ${cfg.dot} ${compact ? "h-1.5 w-1.5" : "h-2 w-2"}`} />
      {cfg.label}
    </span>
  );
}

export function SectionCard({ icon, title, description, action, children, className = "" }) {
  return (
    <section className={`ui-card-static overflow-hidden !p-0 ${className}`}>
      <div className="flex flex-col gap-3 border-b border-slate-100 px-6 py-5 sm:flex-row sm:items-start sm:justify-between dark:border-slate-800">
        <div className="flex min-w-0 items-start gap-3">
          {icon && (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-100 dark:bg-teal-950/50 dark:text-teal-300 dark:ring-teal-900/50">
              <i className={`${icon} text-lg`} aria-hidden />
            </span>
          )}
          <div className="min-w-0">
            <h3 className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-100">{title}</h3>
            {description && (
              <div className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500">{description}</div>
            )}
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children && <div className="px-6 py-5 sm:px-6 sm:py-6">{children}</div>}
    </section>
  );
}

export function UrlDisplay({ label, url, hint }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    const ok = await copyTextToClipboard(url);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="group rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/80 p-4 ring-1 ring-slate-100 transition-shadow hover:shadow-sm dark:border-slate-700/80 dark:from-slate-900 dark:to-slate-900/50 dark:ring-slate-800">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
        {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className="min-w-0 flex-1 overflow-hidden rounded-lg bg-slate-900/95 px-3 py-2.5 dark:bg-slate-950">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block truncate font-mono text-sm text-teal-300 hover:text-teal-200"
          >
            {url}
          </a>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="ui-btn-secondary !px-3 !py-2.5 shrink-0"
          aria-label={`Copy ${label}`}
        >
          <i className={copied ? "ri-check-line text-emerald-600" : "ri-file-copy-line"} aria-hidden />
          {copied ? "Copied" : "Copy"}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="ui-btn-icon shrink-0"
          aria-label={`Open ${label}`}
        >
          <i className="ri-external-link-line" aria-hidden />
        </a>
      </div>
    </div>
  );
}

export function DnsRecordCard({ record }) {
  const [copied, setCopied] = useState(null);

  async function handleCopy(field, value) {
    const ok = await copyTextToClipboard(value);
    if (!ok) return;
    setCopied(field);
    window.setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="rounded-xl border border-slate-200/80 bg-white p-4 ring-1 ring-slate-100 dark:border-slate-700/80 dark:bg-slate-900/50 dark:ring-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-slate-900 px-2 py-1 font-mono text-[11px] font-bold uppercase tracking-wide text-white dark:bg-slate-700">
            {record.type}
          </span>
          {record.required && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Required</span>
          )}
        </div>
        <p className="text-xs text-slate-500">{record.purpose}</p>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {[
          { key: "name", label: "Host / Name", value: record.name },
          { key: "value", label: "Value / Points to", value: record.value },
        ].map(({ key, label, value }) => (
          <div key={key}>
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
            <div className="flex items-start gap-1 rounded-lg border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-950">
              <code className="min-w-0 flex-1 break-all font-mono text-xs leading-relaxed text-slate-800 dark:text-slate-200">
                {value}
              </code>
              <button
                type="button"
                onClick={() => handleCopy(key, value)}
                className="shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-white hover:text-teal-600 dark:hover:bg-slate-800 dark:hover:text-teal-400"
                aria-label={`Copy ${label}`}
              >
                <i className={copied === key ? "ri-check-line text-emerald-600" : "ri-file-copy-line"} aria-hidden />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ChecklistItem({ step, title, description, done, active }) {
  return (
    <li className="flex gap-3">
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors ${
          done
            ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-800"
            : active
              ? "bg-teal-100 text-teal-700 ring-2 ring-teal-300 dark:bg-teal-950/50 dark:text-teal-300 dark:ring-teal-700"
              : "bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700"
        }`}
      >
        {done ? <i className="ri-check-line" aria-hidden /> : step}
      </span>
      <div className="min-w-0 pt-0.5">
        <p className={`text-sm font-medium ${done ? "text-slate-500 line-through dark:text-slate-500" : "text-slate-800 dark:text-slate-200"}`}>
          {title}
        </p>
        {description && <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{description}</p>}
      </div>
    </li>
  );
}

export function StatChip({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 ring-slate-200/80 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700",
    success: "bg-emerald-50 text-emerald-800 ring-emerald-200/70 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800/50",
    warn: "bg-amber-50 text-amber-800 ring-amber-200/70 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800/50",
    brand: "bg-teal-50 text-teal-800 ring-teal-200/70 dark:bg-teal-950/40 dark:text-teal-300 dark:ring-teal-800/50",
  };

  return (
    <div className={`inline-flex flex-col rounded-xl px-3.5 py-2 ring-1 ${tones[tone] ?? tones.neutral}`}>
      <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
      <span className="mt-0.5 text-sm font-semibold">{value}</span>
    </div>
  );
}
